import { httpPost } from "../../../../../Utils/apis/apis";

// --- Constants ---
export const DEFAULT_FETCH_SP_NAME = "DF.usp_GetLoanStages";
export const LOOKUP_SP_NAME = "DF.usp_FormLookupField";
export const SAVE_SP_NAME = "DF.usp_AddLoanStages";
export const REVIVE_SP_NAME = "DF.usp_ReviveNLPLoan";
export const LOAN_CLOSED_RECEIVABLE_SP_NAME = "DF.usp_GetLoanClosedReceivable";
export const CROSS_COLLATERALISATION_SP_NAME =
  "DF.usp_AddCrossCollateralisationCheck";
export const LOOKUP_CUSTOMER = "NLP Customer";
export const LOOKUP_HTB = "NLP HTB";
export const TERMINATED_BADGE = "No Longer Proceeding";
export const LOAN_CLOSED_STAGE = "Loan Closed";

export const TERMINATION_TYPE_OPTIONS = [
  {
    value: LOOKUP_CUSTOMER,
    label: "Customer",
  },
  {
    value: LOOKUP_HTB,
    label: "HTB",
  },
];

export const getTerminationTypeValue = (value = "") => {
  if (value === LOOKUP_CUSTOMER) {
    return "Customer";
  }
  if (value === LOOKUP_HTB) {
    return "HTB";
  }
  return "";
};

export const HARD_CODED_TASKS = [
  { stage: "Initial Enquiry", task: "Cashflow generated?", status: "done" },
  { stage: "Initial Enquiry", task: "Draft completion statement ready?", status: "pending" },
  { stage: "Terms Issued", task: "Indicative loan terms sheet prepared?", status: "pending" },
  { stage: "Terms Issued", task: "Wishlist send to customer?", status: "pending" },
  { stage: "Terms Issued", task: "A&L statement requested from customer?", status: "pending" },
  { stage: "Terms Issued", task: "Personal declaration form send to customer?", status: "pending" },
  { stage: "Terms Issued", task: "SoW/SoF statement requested from customer?", status: "pending" },
  { stage: "Terms Accepted", task: "Confirmation received from customer to proceed?", status: "pending" },
];

// --- Helper Functions ---

export const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map((item) => `${item}`.trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => `${item}`.trim()).filter(Boolean);
      }
    } catch (error) {
      // fall back to comma splitting below
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

export const normalizeMainResponse = (rawResponse) => {
  const items = Array.isArray(rawResponse) ? rawResponse : [rawResponse];
  let firstLevel = items[0] || {};
  
  // If the first level is an array (a table), get the first row
  let source = Array.isArray(firstLevel) ? (firstLevel[0] || {}) : firstLevel;

  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch (error) {
      source = {};
    }
  }

  const currentStage = (source?.["Current Stage"] || source?.currentStage || "").trim();
  const childStages = normalizeArray(
    source?.["Child Stage Name"] || source?.childStages || ""
  );

  return {
    currentStage,
    childStages,
    nextParentStage: (
      source?.["Next Parent Stage"] ||
      source?.NextParentStage ||
      source?.nextParentStage ||
      source?.NextStage ||
      ""
    ).trim(),
    previousSubStage: (
      source?.["Previous Sub Stage"] ||
      source?.PreviousSubStage ||
      source?.previousSubStage ||
      ""
    ).trim(),
    currentStatus: (source?.["Current Status"] || source?.currentStatus || "").trim(),
    isTerminated:
      (source?.["Current Status"] || source?.currentStatus || "").trim() === "No Longer Proceeding" ||
      (source?.["Current Status"] || source?.currentStatus || "").trim() === "Declined" ||
      (source?.["Current Status"] || source?.currentStatus || "").trim() === "NLP"
        ? (source?.["Current Status"] || source?.currentStatus || "").trim()
        : false,
    reason: source?.["Reason"] || source?.["NLP Reason"] || "",
    reasonComments: source?.["Reason Comments"] || source?.["NLP Reason Comments"] || "",
    actionBy: source?.["Created By"] || source?.["Action By"] || "",
    declinedDate: source?.["Date"] || source?.["DeclinedDate"] || "",
    declinedRejectionType: source?.["Rejection Type"] || source?.["Declined Rejection Type"] || "",
    commitmentFees: Number(
      source?.["Commitment Fee Received"] || 
      source?.["Committment Fees"] || 
      source?.["CommitmentFees"] || 
      source?.commitmentFees || 
      0
    ),
  };
};

export const normalizeLookupOptions = (rows = []) =>
  rows
    .map((row) => {
      const label = row?.label || row?.Label || row?.value || row?.Value || "";
      const value = row?.value || row?.Value || label;

      if (!label) {
        return null;
      }

      return {
        label: `${label}`,
        value: `${value}`,
      };
    })
    .filter(Boolean);

export const isOtherOption = (value = "") => {
  const v = `${value}`.trim().toLowerCase();
  return v === "other" || v === "others";
};

export const isRequestFailure = (response) =>
  Boolean(
    response?.isAxiosError ||
      (response?.status && response.status >= 400) ||
      (response?.response?.status && response.response.status >= 400)
  );

// --- API Functions ---

export const executeMultiSp = async (items) => {
  const response = await httpPost(
    "/SQLQuery/getDataFromMultipleSPs?chartSPs=false",
    items,
    {},
    true
  );
  if (isRequestFailure(response)) {
    throw response;
  }

  return response?.data || {};
};

export const updateDealStatus = async (crmId, currentStage, nextStage, userEmail, isBackwardMovement = false) => {
  const response = await httpPost(`/HTBCRMData/updateDealStatus`, {
    id: crmId,
    currentStage: currentStage,
    newStage: nextStage,
    isBackwardMovement: isBackwardMovement,
    storedProcedure: SAVE_SP_NAME,
    parameters: {
      loanId: crmId,
      nlpReason: "",
      nlpReasonComment: "",
      status: nextStage,
      userEmail: userEmail,
      rejectionType: "",
      retainCommitmentFees: "",
    },
  });

  if (isRequestFailure(response)) {
    throw response;
  }

  return response;
};

export const saveNlpDecision = async (payload) => {
  const response = await httpPost("/cars/npw", payload, "", true);

  if (isRequestFailure(response)) {
    throw response?.response;
  }

  return response;
};

export const getLoanClosedReceivables = async (loanId) => {
  const data = await executeMultiSp([
    {
      id: "loanClosedReceivable",
      spName: LOAN_CLOSED_RECEIVABLE_SP_NAME,
      parameters: {
        Loanid: loanId,
      },
    },
  ]);

  const raw = data?.loanClosedReceivable;
  const row = Array.isArray(raw) ? raw[0] : raw;
  return  row || {};
};

export const addCrossCollateralisationCheck = async (loanId) => {
  const data = await executeMultiSp([
    {
      id: "crossCollateralisationCheck",
      spName: CROSS_COLLATERALISATION_SP_NAME,
      parameters: {
        LoanId: loanId,
      },
    },
  ]);

  if (data?.Message || data?.message) {
    throw new Error(data.Message || data.message);
  }

  const raw = data?.crossCollateralisationCheck;
  const result = Array.isArray(raw) ? raw[0] : raw;

  if (result?.Message || result?.message) {
    throw new Error(result.Message || result.message);
  }

  return result;
};

export const reviveLoan = async (loanId, modifiedBy) => {
  const items = [
    {
      id: "reviveLoan",
      spName: REVIVE_SP_NAME,
      parameters: {
        LoanId: loanId,
        ModifiedBy: modifiedBy,
      },
    },
  ];
  return executeMultiSp(items);
};
